import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Utils } from '../../../../utils/utils';
import { HttpClient } from '@angular/common/http';
import { ReleaseNotesDialogComponent } from '../releasenotesdialog/releasenotesdialog.component';
import { ModelFileDialogComponent } from '../modelfiledialog/modelfiledialog.component';
import { ToastrService } from 'ngx-toastr';
import { DefaultValuesService } from '../../../../services/defaultvalues.service';

@Component({
    selector: 'app-controllersoftwaredetails-dialog',
    templateUrl: './controllersoftwaredetails-dialog.component.html',
    styleUrls: ['./controllersoftwaredetails-dialog.component.less'],
    standalone: false
})
export class ControllerSoftwareDetailsDialogComponent implements OnInit {

    filteredFamilyCodes: FamilyCode[] = [];
    selectedFamilyCodeId!: number;
    filteredProductUsages: EMTProductUsageItem[] = [];
    selectedProductUsageId!: number;
    editForm!: FormGroup;
    isCallSucceed: number = 0;
    originalFormValue!: any;
    modelFileContent: string | null = null;
    uploadModelFileName: string = '';
    isContractSpecific: boolean = false;

    constructor(@Inject(MAT_DIALOG_DATA) public data: {
        softwareReleaseForm: FormGroup,
        availableFamilyCodes: FamilyCode[],
        availableProductUsages: EMTProductUsageItem[],
        fileUploadId: string,
        availableLanguages: Language[],
        isContractSpecific?: boolean
    },
        private readonly fb: FormBuilder
        , private readonly dialogRef: MatDialogRef<ControllerSoftwareDetailsDialogComponent>
        , private readonly http: HttpClient
        , @Inject('BASE_URL') private readonly baseUrl: string
        , private readonly dialogForm: MatDialog
        , private readonly _formBuilder: FormBuilder
        , private readonly toastrService: ToastrService
        , private readonly defaults: DefaultValuesService
    ) {
        this.isContractSpecific = !!data.isContractSpecific;
    }

    ngOnInit(): void {
        // Clone the form value into a new FormGroup
        const originalValue = this.data.softwareReleaseForm.value;
        this.editForm = this.fb.group({
            ...originalValue,
            modelFileName: [originalValue.modelFileName || ''],
            modelFile: [null],
            availableReleaseNotes: this.fb.array(
                (originalValue.availableReleaseNotes ?? []).map(note =>
                    this.fb.group({
                        softwareReleaseId: [note.softwareReleaseId],
                        softwareReleaseNotesId: [note.softwareReleaseNotesId],
                        language: [note.language],
                        fileName: [note.fileName],
                        isEnabled: [note.isEnabled],
                        createdByUser: [note.createdByUser],
                        createdAtUtc: [note.createdAtUtc],
                        changedAtUtc: [note.changedAtUtc],
                        changedByUser: [note.changedByUser],
                        file: [null]
                    })
                )
            ),
            fileUploadId: this.data.fileUploadId,
        });



        this.originalFormValue = JSON.parse(JSON.stringify(this.editForm.value));
        this.selectedFamilyCodeId = this.editForm.value.familyCodeId;
        this.selectedProductUsageId = this.editForm.value.productUsageId;
        this.filteredFamilyCodes = this.data.availableFamilyCodes;
        this.filteredProductUsages = this.data.availableProductUsages;
    }

    triggerModelFileInput() {
        const input = document.getElementById('modelFileInput') as HTMLInputElement;
        if (input) {
            input.click();
        }
    }

    getFamilyCodeText(familyCodeId: number) {
        let famcode = this.data.availableFamilyCodes.find(x => x.familyCodeId === familyCodeId);
        if (famcode)
            return `${famcode.familyCode} - ${famcode.familyCodeName}`;
        else
            return "";
    }

    getProductUsageText(productusageId: number) {
        let prodUsage = this.data.availableProductUsages.find(x => x.productUsageID === productusageId);
        if (prodUsage)
            return prodUsage.name;
        else
            return "";
    }

    onFamilyCodesFilter(event: Event) {
        const value = (event.target as HTMLInputElement).value;
        this.filterFamilyCodes(value.toLowerCase());
    }

    filterFamilyCodes(value: string) {

        // If value is empty, user cleared the input
        if (!value) {
            this.selectedFamilyCodeId = -1;
            return;
        }

        this.filteredFamilyCodes = this.data.availableFamilyCodes.filter(fc =>
            this.getFamilyCodeText(fc.familyCodeId).toLowerCase().includes(value.toLowerCase())
        );

        // Check if value matches any family code
        const matchedFamilyCode = this.filteredFamilyCodes.find(
            fc => this.getFamilyCodeText(fc.familyCodeId).toLowerCase() === value.toLowerCase()
        );
        if (!matchedFamilyCode) {
            this.selectedFamilyCodeId = -1;
            return;
        } else {
            this.selectedFamilyCodeId = matchedFamilyCode.familyCodeId;
        }
    }

    onProductUsageFilter(event: Event) {
        const value = (event.target as HTMLInputElement).value;
        this.filterProductUsages(value.toLowerCase());
    }

    filterProductUsages(value: string) {
        this.filteredProductUsages = this.data.availableProductUsages.filter(pu =>
            this.getProductUsageText(pu.productUsageID).toLowerCase().includes(value.toLowerCase())
        );
    }

    onFamilyCodeSelected(event: MatAutocompleteSelectedEvent) {
        const selectedId = event.option.value;
        this.editForm.patchValue({ familyCodeId: selectedId });
        this.selectedFamilyCodeId = selectedId;
    }

    onFamilyCodeFormFieldClick() {
        this.selectedFamilyCodeId = -1;
        this.filteredFamilyCodes = this.data.availableFamilyCodes; // Reset filter
    }

    onProductUsageSelected(event: MatAutocompleteSelectedEvent) {
        const selectedId = event.option.value;
        this.editForm.patchValue({ productUsageId: selectedId });
        this.selectedProductUsageId = selectedId;
    }

    onProductUsageFormFieldClick() {
        this.selectedProductUsageId = -1;
        this.filteredProductUsages = this.data.availableProductUsages; // Reset filter
    }

    onFileChange(event: any) {
        const files = (event?.target as HTMLInputElement)?.files;
        if (files && files.length) {
            const file = files[0];
            this.editForm.get('softwareFile')?.setValue(file);
            this.editForm.get('softwareFileName')?.setValue(file.name);
        } else {
            this.editForm.get('softwareFile')?.setValue(null);
        }
    }

    onModelFileChange(event: Event) {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files.length > 0) {
            const file = input.files[0];
            this.uploadModelFileName = file.name;
            this.editForm.patchValue({ modelFile: file });
            const reader = new FileReader();
            reader.onload = () => {
                this.modelFileContent = reader.result as string;
            };
            reader.readAsText(file);
        }
    }

    deleteModelFile() {
        // Call endpoint to remove link in database immediately
        Utils.httpUpdateControllerSoftwareModelFile(
            this.http,
            this.baseUrl,
            this.editForm.value.controllerSoftwareVersionID,
            0, // Pass 0 to remove the link
            this,
            (tthis: ControllerSoftwareDetailsDialogComponent, result: any) => {
                if (result && !result.error) {
                    // Clear locally
                    tthis.uploadModelFileName = '';
                    tthis.modelFileContent = null;
                    tthis.editForm.patchValue({
                        modelFile: null,
                        modelFileId: 0,
                        modelFileName: ''
                    });
                    
                    // Also update the parent form
                    tthis.data.softwareReleaseForm.patchValue({
                        modelFileId: 0,
                        modelFileName: ''
                    });
                    
                    tthis.toastrService.success('Model file unlinked successfully!');
                } else {
                    tthis.toastrService.error(result?.errorMssg || 'Failed to unlink model file');
                }
            }
        );
    }

    downloadModelFile() {
        const modelFileId = this.editForm.value.modelFileId;
        if (this.editForm.value.modelFileId) {
            Utils.httpDownloadControllerSoftwareModelFile(
                this.http,
                this.baseUrl,
                this.editForm.value.modelFileId,
                this,
                (tthis: ControllerSoftwareDetailsDialogComponent, result: any) => {
                    if (result && !result.error) {
                        // Download the file
                        const blob = new Blob([result], { type: 'application/json' });
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = tthis.editForm.value.modelFileName || 'downloaded-model.json';
                        a.click();
                        window.URL.revokeObjectURL(url);
                    } else {
                        tthis.toastrService.error(result.errorMssg || 'Model file download failed.');
                    }
                }
            );
        }
    }
    cancelModelFile(input?: HTMLInputElement) {
        this.uploadModelFileName = '';
        this.editForm.patchValue({ modelFile: null });
        this.modelFileContent = null;
        if (input) {
            input.value = '';
        }
    }

    validateSoftwareFile(): boolean {
        const softwareFile: File | null = this.editForm.value.softwareFile;
        if (!softwareFile) {
            return true;
        }

        const fileName = softwareFile.name;
        const softwareVersion: string = this.editForm.value.softwareVersion ?? '';
        let isValid = true;

        if (!fileName.toLowerCase().endsWith('.scp')) {
            this.toastrService.warning(
                `File "${fileName}" must have a ".scp" extension.`,
                'Invalid file extension'
            );
            isValid = false;
        }

        if (softwareVersion && !fileName.includes(softwareVersion)) {
            this.toastrService.warning(
                `File name "${fileName}" must contain the software version "${softwareVersion}".`,
                'File name mismatch'
            );
            isValid = false;
        }

        return isValid;
    }

    onSave() {
        if (this.pendingChanges()) {
            if (!this.validateSoftwareFile()) {
                return;
            }
            this.saveChanges();
        }

        if (this.pendingChangesModelFile()) {
            this.saveModelFileChanges();
        }
    }

    saveChanges() {
        let formData: FormData = new FormData();

        formData.append(this.editForm.value.fileUploadId, this.editForm.value.softwareFile);
        let modifiedSoftwareReleases: EMTControllerSoftwareReleaseItem[] = [];
        let newSoftwareReleases: EMTControllerSoftwareReleaseItem[] = [];
        this.editForm.get('familyCodeId')?.setValue(this.selectedFamilyCodeId);
        if (this.editForm.value.isNewRow) {
            const newRelease = { ...this.editForm.value, isContractSpecific: this.isContractSpecific };
            newSoftwareReleases.push(newRelease);
        } else {
            modifiedSoftwareReleases.push(this.editForm.value);
        }

        let releasesChanges = { 'modifiedReleases': modifiedSoftwareReleases, 'newReleases': newSoftwareReleases }
        formData.append('releasesChanges', JSON.stringify(releasesChanges));

        Utils.httpUpdateControllerSoftwareReleases(
            this.http
            , this.baseUrl
            , formData
            , this
            , function (tthis: ControllerSoftwareDetailsDialogComponent, result: UpsertSoftwareReleaseResponseDto) {
                tthis.dialogRef.close({
                    success: tthis.isCallSucceed === 1,
                    errorMssg: result?.error && result.errorMssg ? result.errorMssg : null
                });
            }
        )
    }

    saveModelFileChanges() {
        // Look for changes related with Model File
        let formData: FormData = new FormData();

        // Attach software file if present
        if (this.editForm.value.softwareFile) {
            formData.append(this.editForm.value.fileUploadId, this.editForm.value.softwareFile);
        }

        // Attach model file if present (JSON)
        if (this.editForm.value.modelFile && this.uploadModelFileName) {
            formData.append('file', this.editForm.value.modelFile, this.uploadModelFileName);
        }
        if (this.editForm.value.modelFileId) {
            formData.append('ModelFileId', this.editForm.value.modelFileId);
        }
        formData.append('FilePath', this.uploadModelFileName ?? '');
        formData.append('Comment', this.editForm.value.comment ?? '');
        formData.append('ReleaseDate', this.editForm.value.releaseDate ?? '');
        formData.append('UploadedBy', this.editForm.value.modifUser ?? '');
        formData.append('ControllerSoftwareVersionID', this.editForm.value.controllerSoftwareVersionID);

        // Prepare release changes
        let modifiedSoftwareReleases: EMTControllerSoftwareReleaseItem[] = [];
        let newSoftwareReleases: EMTControllerSoftwareReleaseItem[] = [];
        this.editForm.get('familyCodeId')?.setValue(this.selectedFamilyCodeId);

        if (this.editForm.value.isNewRow) {
            const newRelease = { ...this.editForm.value };
            newSoftwareReleases.push(newRelease);
        } else {
            modifiedSoftwareReleases.push(this.editForm.value);
        }

        let releasesChanges = { 'modifiedReleases': modifiedSoftwareReleases, 'newReleases': newSoftwareReleases };
        formData.append('releasesChanges', JSON.stringify(releasesChanges));

        Utils.httpUploadControllerSoftwareModelFile(
            this.http,
            this.baseUrl,
            formData,
            String(this.selectedFamilyCodeId),
            this,
            (tthis: ControllerSoftwareDetailsDialogComponent, result: any) => {
                if (result && !result.error) {
                    tthis.toastrService.success('Model file uploaded successfully!');
                    tthis.dialogRef.close({
                        success: true,
                        errorMssg: null
                    });
                } else {
                    tthis.toastrService.error(result.errorMssg || 'Model file upload failed.');
                }
            }
        );
    }

    onCancel(): void {
        this.dialogRef.close({
            canceled: true,
            updatedReleaseNotes: this.editForm.get('availableReleaseNotes')?.value
        });
    }

    anyChange(): boolean {
        return this.pendingChanges() || this.pendingChangesModelFile();
    }

    pendingChanges(): boolean {
        return this.diffValues()
            && this.selectedFamilyCodeId !== -1
            && this.editForm.value.softwareVersion !== "" && this.editForm.value.softwareVersion.length <= 50
            && this.editForm.value.tdcAgentVersion !== "" && this.editForm.value.tdcAgentVersion.length <= 10
            && this.editForm.value.controllerSoftwareVersion !== "" && this.editForm.value.controllerSoftwareVersion.length <= 10
            && this.editForm.value.productUsageId !== -1
            && (this.editForm.value.isNewRow ? this.editForm.value.softwareFile != null : true);
    }

    pendingChangesModelFile(): boolean {
        return !!this.uploadModelFileName && !!this.editForm.value.modelFile;
    }

    diffValues(): boolean {
        const current = this.editForm.value;
        const original = this.originalFormValue;

        return this.selectedFamilyCodeId !== original.familyCodeId
            || current.softwareVersion !== original.softwareVersion
            || current.tdcAgentVersion !== original.tdcAgentVersion
            || current.controllerSoftwareVersion !== original.controllerSoftwareVersion
            || current.releaseName !== original.releaseName
            || current.productUsageId !== original.productUsageId
            || current.softwareFileName !== original.softwareFileName;
    }

    openReleaseNotes(formGroup: FormGroup | null, isNew: boolean = false) {
        const dialogRef = this.dialogForm.open(ReleaseNotesDialogComponent, {
            height: 'auto',
            width: '600px',
            data: {
                releaseNotesForm: formGroup ?? this._formBuilder.group({
                    fileName: new FormControl(''),
                    file: new FormControl(null),
                }),
                availableLanguages: isNew ? this.data.availableLanguages.filter(lang =>
                    !this.editForm.value.availableReleaseNotes?.some(
                        note => note.language === lang.LanguageCultureName
                    )
                ) : this.data.availableLanguages,
                isNew,
                softwareReleaseId: this.editForm.value.controllerSoftwareVersionID,
                softwareType: 'controller',
                metadataLabel: this.editForm.value.controllerSoftwareVersion
            },
            panelClass: 'details-dialog-panel',
            disableClose: true,
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result?.success && result.updatedReleaseNotes) {
                const releaseNotesArray = new FormArray(
                    result.updatedReleaseNotes.map(note =>
                        this._formBuilder.group({
                            softwareReleaseNotesId: [note.softwareReleaseNotesId],
                            softwareReleaseId: [note.softwareReleaseId],
                            language: [note.language],
                            fileName: [note.fileName],
                            isEnabled: [note.isEnabled],
                            createdByUser: [note.createdByUser],
                            createdAtUtc: [note.createdAtUtc],
                            changedAtUtc: [note.changedAtUtc],
                            changedByUser: [note.changedByUser],
                            file: [null]
                        })
                    )
                );

                // Replace the old FormArray with the new one so the UI refreshes
                this.editForm.setControl('availableReleaseNotes', releaseNotesArray);
                this.editForm.updateValueAndValidity();

                this.toastrService.success('Release notes successfully saved!')
            }
            else if (!result?.canceled) {
                this.toastrService.error('Save Release notes could not be performed. Please, contact with administrator.')
            }
            else {
                const releaseNotesArray = this.editForm.get('availableReleaseNotes') as FormArray;
                if (releaseNotesArray && formGroup) {
                    // Get the softwareReleaseNotesId safely
                    let targetId: any;
                    if (formGroup instanceof FormGroup) {
                        targetId = formGroup.get('softwareReleaseNotesId')?.value;
                    } else {
                        targetId = formGroup['softwareReleaseNotesId'];
                    }
                    // Find the index of the release note to update
                    const idx = releaseNotesArray.controls.findIndex(ctrl =>
                        ctrl.get('softwareReleaseNotesId')?.value === targetId
                    );
                    if (idx !== -1) {
                        releaseNotesArray.at(idx).get('changedByUser')?.setValue(result.changedByUser);
                        this.editForm.updateValueAndValidity();
                    }
                }
            }

        });

    }

    openExistingModelFile() {
        // Reload controller software data to get latest model file information
        const controllerSoftwareId = this.editForm.value.controllerSoftwareVersionID;
        if (!controllerSoftwareId) {
            this.toastrService.error('Invalid controller software ID');
            return;
        }

        Utils.httpGetEMTControllerSoftwareReleaseById(
            this.http,
            this.baseUrl,
            controllerSoftwareId,
            this.isContractSpecific,
            this,
            (tthis: ControllerSoftwareDetailsDialogComponent, result: any) => {
                if (result && !result.error) {
                    // Update editForm with latest model file data
                    tthis.editForm.patchValue({
                        modelFileId: result.modelFileId ?? null,
                        modelFileName: result.modelFileName ?? '',
                        modelFileReleaseName: result.modelFileReleaseName ?? '',
                        modelFileComment: result.modelFileComment ?? '',
                        modelFileCreatedByUser: result.modelFileCreatedByUser ?? '',
                        modelFileChangedByUser: result.modelFileChangedByUser ?? null,
                        modelFileCreatedAtUtc: result.modelFileCreatedAtUtc ?? null,
                        modelFileChangedAtUtc: result.modelFileChangedAtUtc ?? null,
                        modelFilePath: result.modelFilePath ?? ''
                    });

                    // Build the form group with fresh data
                    const modelFileFormGroup = tthis._formBuilder.group({
                        fileName: [result.modelFileName ?? ''],
                        modelFileId: [result.modelFileId ?? null],
                        file: [null],
                        releaseName: [result.modelFileReleaseName ?? ''],
                        comment: [result.modelFileComment ?? ''],
                        createdByUser: [result.modelFileCreatedByUser ?? ''],
                        changedByUser: [result.modelFileChangedByUser ?? null],
                        createdAtUtc: [result.modelFileCreatedAtUtc ?? null],
                        changedAtUtc: [result.modelFileChangedAtUtc ?? null],
                        selectedModelFilePath: [result.modelFilePath ?? '']
                    });
                    tthis.openModelFiles(modelFileFormGroup, false);
                } else {
                    tthis.toastrService.error(result?.errorMssg || 'Failed to load model file data');
                }
            }
        );
    }

    openModelFiles(formGroup: FormGroup | null, isNew: boolean = false) {
        const dialogRef = this.dialogForm.open(ModelFileDialogComponent, {
            height: 'auto',
            width: '600px',
            data: {
                modelFileForm: formGroup ?? this._formBuilder.group({
                    fileName: new FormControl(''),
                    file: new FormControl(null),
                }),
                isNew,
                softwareReleaseId: this.editForm.value.controllerSoftwareVersionID,
                familyCodeId: this.selectedFamilyCodeId,
                metadataLabel: this.editForm.value.controllerSoftwareVersion,
                modifUser: this.defaults.session?.UserUPN,
                comment: this.editForm.value.comment,
                releaseDate: this.editForm.value.releaseDate,
                releaseData: this.editForm.value
            },
            panelClass: 'details-dialog-panel',
            disableClose: true,
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result?.success) {
                this.toastrService.success('Model file successfully saved!');

                if (result.modelFileId && (result.releaseName !== undefined || result.comment !== undefined) && !result.fileName) {
                    // Case 1: Updated release name or comment only
                    const updateData: any = {};
                    if (result.releaseName !== undefined) {
                        updateData.modelFileReleaseName = result.releaseName;
                    }
                    if (result.comment !== undefined) {
                        updateData.modelFileComment = result.comment;
                    }
                    this.editForm.patchValue(updateData);
                    this.data.softwareReleaseForm.patchValue(updateData);
                } else if (result.modelFileId && result.fileName) {
                    // Case 2: Updated model file (new upload or selection)
                    this.editForm.patchValue({
                        modelFileId: result.modelFileId,
                        modelFileName: result.fileName,
                        modelFileReleaseName: result.releaseName || this.editForm.value.modelFileReleaseName
                    });
                    this.uploadModelFileName = result.fileName;
                    this.data.softwareReleaseForm.patchValue({
                        modelFileId: result.modelFileId,
                        modelFileName: result.fileName,
                        modelFileReleaseName: result.releaseName || this.data.softwareReleaseForm.value.modelFileReleaseName
                    });
                } else {
                    // Case 3: Delete - modelFileId === 0, clean
                    this.editForm.patchValue({
                        modelFileId: 0,
                        modelFileName: ''
                    });
                    this.uploadModelFileName = '';
                    this.data.softwareReleaseForm.patchValue({
                        modelFileId: 0,
                        modelFileName: ''
                    });
                }
            } else if (!result?.canceled && result?.errorMssg) {
                this.toastrService.error(result.errorMssg);
            }
        });

    }


    getReleaseNoteLabelText(language: string) {
        const lang = this.data.availableLanguages.find(l => l.LanguageCultureName === language);
        if (lang) {
            // Get first part of lang.Name before '-'
            const namePart = lang.Name.split('-')[0].trim();
            // Get second part of lang.LanguageCultureName after '-'
            const cultureParts = lang.LanguageCultureName.split('-');
            const culturePart = cultureParts.length > 1 ? cultureParts[1].trim() : '';
            return `${namePart} - ${culturePart}`;
        } else {
            return "";
        }
    }
}
